import { Assert } from "../../../../../Assert";
import { DataSource, IDataSource } from "../../../../../Data/DataSource";
import { DataSourcePropertyValueChange } from "../../../../../Data/DataSourcePropertyValueChange";
import { PropertyComparer } from "../../../../../Evaluation/PropertyComparer";
import { ArrayHelper } from "../../../../../Helpers/ArrayHelper";
import { StringHelper } from "../../../../../Helpers/StringHelper";
import { IJNativeEvent } from "../../../../../JNative";
import { PropertyChange } from "../../../../../PropertyChange";
import { Property, PropertyObject, PropertyString, RadObject } from "../../../../../RadObject";
import { Control, IControl } from "../../../../../Ui/Controls/Control";
import { Evaluator } from "../../../SeriouslyRAD.Framework/Evaluation/Evaluator";
import { EvaluatorPropertyValues, IEvaluator } from "../../../SeriouslyRAD.Framework/Evaluation/IEvaluator";
import { NAMESPACE } from "./_Namespace";

export type PropertyEvaluatorPropertyValues =
    EvaluatorPropertyValues
    & {
        comparerId: string;
        dataSource: any;
        propertyName: string;
        propertyValue: any;
    };

export type PropertyEvaluatorProperties = {
    [name in keyof PropertyEvaluatorPropertyValues]: Property<PropertyEvaluatorPropertyValues[name]>
};

export interface IPropertyEvaluator extends IEvaluator, PropertyEvaluatorProperties
{
}
export class PropertyEvaluator extends Evaluator<PropertyEvaluatorPropertyValues> implements IPropertyEvaluator
{
    public static override classNamespace = NAMESPACE + "PropertyEvaluator";
    public static override className = "PropertyEvaluator";

    // #region Properties

    public comparerId!: PropertyString;
    public dataSource!: PropertyObject;
    public propertyName!: PropertyString;
    public propertyValue!: PropertyObject;

    // #endregion

    // #region Protected Methods

    protected _comparerIdChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _dataSourceChanged(change: PropertyChange<any>)
    {
        if (change.originalValue)
            change.originalValue.off("propertyValueChanged", { self: this }, this.onDataSourcePropertyValueChanged);

        if (change.currentValue)
            change.currentValue.on("propertyValueChanged", { self: this }, this.onDataSourcePropertyValueChanged);

        this.evaluate();
    }
    protected _propertyNameChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _propertyValueChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: PropertyEvaluatorPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.comparerId = new PropertyString(this, propertyValues.comparerId, this._comparerIdChanged);
        this.dataSource = new PropertyObject(this, propertyValues.dataSource, this._dataSourceChanged);
        this.propertyName = new PropertyString(this, propertyValues.propertyName, this._propertyNameChanged);
        this.propertyValue = new PropertyObject(this, propertyValues.propertyValue, this._propertyValueChanged);
    }

    protected override _contextChanged(change: PropertyChange<IControl | IDataSource>)
    {
        if (change.originalValue instanceof Control)
            change.originalValue.off("dataSourceChanged", { self: this }, this.onContextDataSourceChanged);

        if (change.currentValue instanceof Control)
            change.currentValue.on("dataSourceChanged", { self: this }, this.onContextDataSourceChanged);

        this.dataSource.set(change.currentValue instanceof Control ? change.currentValue.dataSource.get() : null);

        super._contextChanged(change);
    }

    public override clone(): this
    {
        const
            evaluator = super.clone() as this;

        evaluator.comparerId.set(this.comparerId.get());
        evaluator.propertyName.set(this.propertyName.get());
        evaluator.propertyValue.set(this.propertyValue.get());

        return evaluator;
    }
    public override evaluate()
    {
        let
            propertyValue = this.propertyValue.get();

        // TODO: BODGE! Recode this!
        if (propertyValue === "true")
            propertyValue = true;
        else if (propertyValue === "false")
            propertyValue = false;

        const
            context = this.context.get(),
            dataSource = context instanceof DataSource ? context : (context instanceof Control && context.dataSource ? context.dataSource.get() : null),
            comparerId = this.comparerId.get(),
            comparer = ArrayHelper.single(PropertyComparer.propertyComparers, item => item.id === comparerId).instance,
            propertyName = this.propertyName.get();

        Assert.isNotNullOrUndefined(propertyName, "propertyName");

        const
            value =
                dataSource
                    ? comparer.compare(dataSource.propertyValueByPathGet(StringHelper.toLowerCamelCase(propertyName)), propertyValue)
                    : false;

        this.value.set(value);

        return value;
    }

    // #endregion

    // #region Event Handlers

    protected onContextDataSourceChanged(evt: IJNativeEvent, changeInfo: PropertyChange<DataSource>)
    {
        const
            self = evt.data.self as this;

        self.dataSource.set(changeInfo.currentValue);
    }
    protected onDataSourcePropertyValueChanged(evt: IJNativeEvent, change: DataSourcePropertyValueChange<any>)
    {
        const
            self = evt.data.self as this;

        if (change.path === StringHelper.toLowerCamelCase(self.propertyName.get() ?? StringHelper.empty))
            self.evaluate();
    }

    // #endregion
}

